import React, { useState, useEffect } from "react";
import { Modal, Text, Button, Alert, Stack, Group } from "@mantine/core";
import QRCode from "qrcode";

interface KeyQrModalProps {
  opened: boolean;
  onClose: () => void;
  privateKeyArmored: string;
  keyName: string;
}

const CHUNK_SIZE = 900; // Conservative limit for reliable scanning

export function KeyQrModal({
  opened,
  onClose,
  privateKeyArmored,
  keyName,
}: KeyQrModalProps) {
  const [qrUrl, setQrUrl] = useState<string | null>(null);
  const [chunks, setChunks] = useState<string[]>([]);
  const [currentChunkIndex, setCurrentChunkIndex] = useState(0);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!opened || !privateKeyArmored) {
      setQrUrl(null);
      setChunks([]);
      return;
    }

    setGenerating(true);

    // Chunking logic
    const totalLength = privateKeyArmored.length;
    const totalChunks = Math.ceil(totalLength / CHUNK_SIZE);
    const newChunks: string[] = [];

    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = start + CHUNK_SIZE;
      const chunkContent = privateKeyArmored.substring(start, end);

      // Format: [Current/Total]Content
      // e.g., [1/3]-----BEGIN PGP...
      if (totalChunks > 1) {
        newChunks.push(`[${i + 1}/${totalChunks}]${chunkContent}`);
      } else {
        newChunks.push(chunkContent);
      }
    }

    setChunks(newChunks);
    setCurrentChunkIndex(0);
    setGenerating(false);
  }, [opened, privateKeyArmored]);

  // Generate QR when current chunk changes
  useEffect(() => {
    if (chunks.length > 0) {
      QRCode.toDataURL(
        chunks[currentChunkIndex],
        { errorCorrectionLevel: "L", width: 400 },
        (err, url) => {
          if (err) {
            console.error(err);
          } else {
            setQrUrl(url);
          }
        },
      );
    }
  }, [chunks, currentChunkIndex]);

  const handleNext = () => {
    if (currentChunkIndex < chunks.length - 1) {
      setCurrentChunkIndex((prev) => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentChunkIndex > 0) {
      setCurrentChunkIndex((prev) => prev - 1);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Export Key via QR" centered>
      <Stack align="center" gap="md">
        <Alert color="orange" title="Security Warning">
          Scanning this QR code will transfer your <b>Private Key</b>. Only scan
          this with your own device.
        </Alert>

        {generating ? (
          <Text>Generating...</Text>
        ) : qrUrl ? (
          <React.Fragment>
            <img
              src={qrUrl}
              alt="Private Key QR Code"
              style={{ width: "100%", maxWidth: 300, border: "1px solid #eee" }}
            />

            {chunks.length > 1 && (
              <Stack gap="xs" align="center" w="100%">
                <Text size="sm" fw={700}>
                  Part {currentChunkIndex + 1} of {chunks.length}
                </Text>
                <Group w="100%" grow>
                  <Button
                    variant="default"
                    onClick={handlePrev}
                    disabled={currentChunkIndex === 0}
                  >
                    Previous
                  </Button>
                  <Button
                    onClick={handleNext}
                    disabled={currentChunkIndex === chunks.length - 1}
                  >
                    Next Part
                  </Button>
                </Group>
              </Stack>
            )}

            <Text size="sm" ta="center" c="dimmed">
              {chunks.length > 1
                ? "Scan each part sequentially to reconstruct the key."
                : "Scan with Signed+ mobile app or compatible PGP tool."}
            </Text>
          </React.Fragment>
        ) : (
          <Text c="red">Error generating QR code</Text>
        )}

        <Button fullWidth variant="subtle" onClick={onClose} mt="sm">
          Close
        </Button>
      </Stack>
    </Modal>
  );
}
